/**
 * Organization Detail Component
 *
 * Displays detailed information for a single organization including:
 * - Profile header with name, status, and Edit button
 * - Information cards (General Info, Contact Info)
 * - Address list
 * - Edit form in a dialog (not inline)
 * - Back navigation to organization list
 */

import { useNavigate } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { AppLogger } from '@/lib/logger'
import { ProfileHeader, ProfileSection } from '@/components/sensnet/ProfileLayout'
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  Building2,
  BarChart2,
  Mail,
  Phone,
  Globe,
  MapPin,
  FileText,
  Briefcase,
} from 'lucide-react'
import { useApiQuery } from '@/api/ApiQueryServiceProvider'
import { AddressList } from './AddressList'
import { FarmgridAccountCard } from './FarmgridAccountCard'
import type { Address, GetOrganizationResponse, Organization } from '@/types/organizations'

const log = AppLogger.getLogger('components/organizations/OrganizationDetail')

export interface OrganizationDetailProps {
  organizationId: number
  onBack?: () => void
}

export function OrganizationDetail({ organizationId, onBack }: OrganizationDetailProps) {
  const navigate = useNavigate()

  log.debug('[OrganizationDetail] Rendered with organizationId:', organizationId)

  const { data: organizationData, isLoading, error, refetch } = useApiQuery<GetOrganizationResponse>(
    'getOrganizationByNumber',
    { orgNumber: organizationId },
  )

  const { data: addressesData, refetch: refetchAddresses } = useApiQuery<{ content: { addresses: Address[] } }>(
    'getAddressesByOrgNumber',
    { orgNumber: organizationId },
  )

  const rawOrganization = organizationData?.content?.organization as Partial<Organization> | undefined

  const rawAddresses = addressesData?.content?.addresses || []
  const mappedAddresses: Address[] = rawAddresses.map((addr) => ({
    id: addr.addressNumber?.toString() || '',
    addressNumber: addr.addressNumber,
    orgNumber: addr.orgNumber,
    organizationId: addr.orgNumber?.toString() || organizationId.toString(),
    addressName: addr.addressName,
    addressType: addr.addressType as Address['addressType'],
    phone: addr.phone,
    doorNumber: addr.doorNumber,
    addressLine1: addr.addressLine1,
    addressLine2: addr.addressLine2,
    neighborhood: addr.neighborhood,
    street: addr.addressLine1 || addr.street,
    city: addr.city,
    state: addr.state,
    postalCode: addr.postalCode,
    country: addr.country,
    isArchived: !!addr.archiveDate || addr.isArchived || false,
    archiveDate: addr.archiveDate,
    createdAt: addr.createdAt,
    updatedAt: addr.updatedAt,
  }))

  const organization: Organization | undefined = rawOrganization ? {
    ...rawOrganization,
    email: rawOrganization.emailId || rawOrganization.email,
    isArchived: !!rawOrganization.archiveDate || rawOrganization.isArchived || false,
    createdAt: rawOrganization.createdAt,
    updatedAt: rawOrganization.updatedAt,
    addresses: mappedAddresses,
  } as Organization : undefined

  const handleBack = () => {
    if (onBack) {
      onBack()
    } else {
      navigate({ to: '/organizations' })
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="text-muted-foreground">Loading organization details...</span>
        </div>
      </div>
    )
  }

  if (error || !organization) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <p className="text-lg font-medium">Failed to load organization</p>
        <p className="text-sm text-muted-foreground">{error?.message || 'Organization not found'}</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleBack}>Back to Organizations</Button>
          <Button onClick={() => refetch()}>Retry</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Back button + Header (matches Network Corridor profile) */}
      <div className="flex flex-col gap-3">
        <div>
          <Button variant="ghost" size="sm" onClick={handleBack} className="gap-2 -ml-2">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        </div>
        <ProfileHeader
          title={organization.name}
          subtitle={`Organization Number: ${organization.orgNumber}`}
          badge={organization.isArchived ? 'Archived' : 'Active'}
          subtitleIcon={<Building2 className="h-4 w-4" />}
        />
      </div>

      {/* Left: General Info + Metrics stacked | Right: Identity panel — mirrors Network Corridor */}
      <div className="grid grid-cols-1 md:grid-cols-2 items-stretch gap-6">
        {/* Left column: two stacked cards */}
        <div className="flex flex-col gap-6">
          <ProfileSection
            title="General Information"
            icon={<Briefcase className="h-4 w-4" />}
            variant="dark"
          >
            <div className="space-y-4 p-5">
              <div>
                <p className="text-xs text-muted-foreground">Organization Name</p>
                <p className="text-lg font-semibold">{organization.name}</p>
                <div className="mt-2 border-b border-slate-200" />
              </div>
              {organization.orgCode && (
                <div>
                  <p className="text-xs text-muted-foreground">Organization Code</p>
                  <p className="text-sm">{organization.orgCode}</p>
                </div>
              )}
              {organization.type && (
                <div>
                  <p className="text-xs text-muted-foreground">Type</p>
                  <p className="text-sm">{organization.type}</p>
                </div>
              )}
              {organization.role && (
                <div>
                  <p className="text-xs text-muted-foreground">Role</p>
                  <p className="text-sm">{organization.role}</p>
                </div>
              )}
              {organization.taxId && (
                <div>
                  <p className="text-xs text-muted-foreground">Tax ID</p>
                  <p className="text-sm">{organization.taxId}</p>
                </div>
              )}
            </div>
          </ProfileSection>

          <ProfileSection
            title="Organization Metrics"
            icon={<BarChart2 className="h-4 w-4" />}
            variant="dark"
          >
            <div className="grid grid-cols-2 divide-x divide-slate-200 p-5">
              <div className="pr-6">
                <p className="text-xs text-muted-foreground">Employee Count</p>
                <p className="text-3xl font-bold text-slate-800">
                  {organization.employeeCount ?? '—'}
                </p>
                <p className="mt-1 text-xs text-slate-400">Total employees</p>
              </div>
              <div className="pl-6">
                <p className="text-xs text-muted-foreground">Addresses</p>
                <p className="text-3xl font-bold text-slate-800">{mappedAddresses.length}</p>
                <p className="mt-1 text-xs text-slate-400">Registered locations</p>
              </div>
            </div>
          </ProfileSection>
        </div>

        {/* Right column: Contact Information (simple card, stretches to full height) */}
        <ProfileSection
          title="Contact Information"
          icon={<Mail className="h-4 w-4" />}
          variant="dark"
          className="flex flex-col"
        >
          <div className="space-y-3 p-5">
            {organization.email && (
              <div>
                <p className="text-xs text-muted-foreground">Email</p>
                <p className="flex items-center gap-1 text-sm">
                  <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                  {organization.email}
                </p>
              </div>
            )}
            {organization.phone && (
              <div>
                <p className="text-xs text-muted-foreground">Phone</p>
                <p className="flex items-center gap-1 text-sm">
                  <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                  {organization.phone}
                </p>
              </div>
            )}
            {organization.website && (
              <div>
                <p className="text-xs text-muted-foreground">Website</p>
                <p className="flex items-center gap-1 text-sm">
                  <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                  {organization.website}
                </p>
              </div>
            )}
            {organization.country && (
              <div>
                <p className="text-xs text-muted-foreground">Country</p>
                <p className="flex items-center gap-1 text-sm">
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                  {organization.country}
                </p>
              </div>
            )}
            {organization.accountManagerId && (
              <div>
                <p className="text-xs text-muted-foreground">Account Manager ID</p>
                <p className="flex items-center gap-1 text-sm">
                  <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                  {organization.accountManagerId}
                </p>
              </div>
            )}
          </div>
        </ProfileSection>
      </div>

      {/* Address List */}
      <AddressList
        organizationId={organizationId.toString()}
        addresses={mappedAddresses}
        isArchived={organization.isArchived}
        onAddressChange={refetchAddresses}
      />

      {/* Farmgrid account — one status-only account per org (vertical model) */}
      <FarmgridAccountCard
        organizationId={organizationId.toString()}
        isArchived={organization.isArchived}
      />
    </div>
  )
}
